import { IcoLens } from 'icone';
import React, { useEffect, useState } from 'react';
import { Accordion, Button, Form, Modal, OverlayTrigger, Tooltip } from 'react-bootstrap';
import { ExternalCostType } from './PeriodicReportingExternalCosts';
import {
	getQuestionsToSettleByProjectIdAndStatementIdAndQuotaId,
	settleChecklistAnswers,
	QuestionToSettleType,
} from 'lib/api_ts/periodicReportingApi';
import { useLocation } from 'react-router-dom-v5-compat';
import StatusBar from 'components/StatusBar';

interface PeriodicReportingInfoCellProps {
	data: ExternalCostType & { checked: number };
	value: number;
	selectedIds: number[];
	statementId: number;
	fetchData: () => void;
	isCoordinator: boolean;
	isEnabledToView: boolean;
}

const PeriodicReportingInfoCell: React.FC<PeriodicReportingInfoCellProps> = ({
	data,
	value,
	selectedIds,
	statementId,
	fetchData,
	isCoordinator,
	isEnabledToView,
}) => {
	const [isModalOpen, setIsModalOpen] = useState(false);
	const [isConfirmModalOpen, setIsConfirmModalOpen] = useState(false);
	const [selectedQuestions, setSelectedQuestions] = useState<number[]>([]);
	const [questionsToSettle, setQuestionsToSettle] = useState<QuestionToSettleType[]>([]);

	const [notesMap, setNotesMap] = useState<Map<number, string>>(new Map());

	const blankStatusBar = {
		status: undefined,
		message: '',
		timeout: undefined,
	};

	const [statusBar, setStatusBar] = useState(blankStatusBar);
	const resetStatusBar = () => {
		setStatusBar(blankStatusBar);
	};

	const { pathname } = useLocation();
	const projectId = pathname.split('/')[2];
	useEffect(() => {
		(async () => {
			if (!isModalOpen) {
				setQuestionsToSettle([]);
				return;
			}
			try {
				const questionsToSettleFromApi = await getQuestionsToSettleByProjectIdAndStatementIdAndQuotaId(projectId, statementId, data.id);
				setQuestionsToSettle(questionsToSettleFromApi);
			} catch (error) {
				console.error('error', error);
			}
		})();
	}, [isModalOpen, projectId, statementId, data.id]);

	const handleSettleQuestions = async () => {
		setStatusBar({
			status: 'elaborating',
			message: 'Validazione elementi in corso',
			timeout: false,
		});
		try {
			await settleChecklistAnswers(
				selectedQuestions.map((questionId) => {
					return {
						id: questionId,
						note: notesMap.get(questionId) || 'Sanato',
					};
				}),
			);

			setStatusBar({
				status: 'success',
				message: 'Elementi validati con successo',
				timeout: true,
			});
			setTimeout(() => {
				setIsConfirmModalOpen(false);
				resetStatusBar();
				fetchData();
			}, 2000);
		} catch (error) {
			setStatusBar({
				status: 'error',
				message: 'Errore durante la validazione degli elementi',
				timeout: true,
			});
			setTimeout(() => {
				resetStatusBar();
			}, 2000);
		}
	};

	const entityMapping = [
		{
			entity: 'Fattura',
			entityModel: 'App\\Models\\Invoice',
		},
		{
			entity: 'Attingimento',
			entityModel: 'App\\Models\\Contract',
		},
		{
			entity: 'Contratto',
			entityModel: 'App\\Models\\FrameworkContract',
		},
		{
			entity: 'Pagamento',
			entityModel: 'App\\Models\\InvoicePayment',
		},
		{
			entity: 'Gara',
			entityModel: 'App\\Models\\Tender',
		},
		{
			entity: 'DNSH',
			entityModel: 'App\\Models\\OTPlant',
		},
	];

	const categories = questionsToSettle.map((questionToSettle) => {
		return {
			label: entityMapping.find((entity) => entity.entityModel === questionToSettle.entityType).entity,
			name: questionToSettle.entityType.split('\\')[2],
			questions: questionToSettle.answers.map((answer) => {
				return {
					id: answer.id,
					label: answer.question.questions,
					description: answer.question.objective,
					note: answer.note,
				};
			}),
		};
	});

	data.checked = selectedIds.includes(data.id) ? 1 : 0;
	return (isCoordinator || isEnabledToView) && value === 1 && data.checked === 1 ? (
		<>
			<OverlayTrigger placement="top" overlay={(props) => <Tooltip {...props}>Info</Tooltip>}>
				<Button
					variant="link"
					onClick={() => {
						setIsModalOpen(true);
					}}
				>
					<IcoLens className="edit" />
				</Button>
			</OverlayTrigger>
			<Modal size="xl" show={isModalOpen} onHide={() => setIsModalOpen(false)} centered>
				<Modal.Header closeButton>
					<Modal.Title>Analisi record {data.id}</Modal.Title>
				</Modal.Header>
				<Modal.Body>
					<Form.Group style={{ display: 'flex', alignItems: 'center' }} className="ms-4 mb-3">
						<Form.Check
							inline
							type="checkbox"
							checked={selectedQuestions.length === categories.flatMap((category) => category.questions).length}
							onChange={(event) => {
								setSelectedQuestions(() => {
									if (event.target.checked) {
										return categories.flatMap((category) => category.questions).map((question) => question.id);
									}
									return [];
								});
							}}
							label={
								<span
									style={{
										borderBottom: '1px solid #b3b3b3',
										color: '#09A8B0',
									}}
								>
									Seleziona tutto
								</span>
							}
						/>
					</Form.Group>
					<div className="checklist-scroll" style={{ minHeight: '50vh', maxHeight: '73vh' }}>
						{categories.map((category) => (
							<Accordion key={category.name} style={{ marginBottom: '1.5rem' }} flush alwaysOpen defaultActiveKey={['0']}>
								<Accordion.Item eventKey="0">
									<Accordion.Header as="h5" className="accordion-title">
										<span style={{ marginLeft: '88px' }}>{category.label}</span>
									</Accordion.Header>
									<Accordion.Body>
										{category.questions.map((question) => (
											<Form key={question.id} className="mb-3">
												<Form.Group
													style={{
														display: 'grid',
														gridTemplateColumns: '88px 1fr',
													}}
												>
													<Form.Check
														type="checkbox"
														checked={selectedQuestions.includes(question.id)}
														onChange={(event) => {
															setSelectedQuestions((prevSelectedQuestions) => {
																if (event.target.checked) {
																	return [...prevSelectedQuestions, question.id];
																}
																return prevSelectedQuestions.filter((id) => id !== question.id);
															});
														}}
													/>
													<Form.Label>
														<div>
															<p className="fw-normal mb-0">{question.label}</p>
															<p className="fw-bold" style={{ fontSize: '.7rem' }}>
																{question.description}
															</p>
														</div>
													</Form.Label>
												</Form.Group>
												<div
													style={{
														marginLeft: '88px',
														display: 'grid',
														gridTemplateColumns: '4fr auto 1fr',
														gap: '24px',
													}}
												>
													<Form.Group>
														<Form.Control
															as="textarea"
															className="form-control"
															placeholder="Non sono presenti note"
															readOnly
															defaultValue={question.note || ''}
														/>
													</Form.Group>
													<Form.Group>
														<Form.Control
															as="textarea"
															className="form-control"
															placeholder="es. Sanato"
															value={notesMap.get(question.id) || ''}
															onChange={(event) => {
																setNotesMap((prevNotes) => new Map(prevNotes).set(question.id, event.target.value));
															}}
														/>
													</Form.Group>
												</div>
											</Form>
										))}
									</Accordion.Body>
								</Accordion.Item>
							</Accordion>
						))}
					</div>
				</Modal.Body>
				<Modal.Footer>
					<Button
						onClick={() => {
							setIsConfirmModalOpen(true);
						}}
						variant="primary"
						disabled={selectedQuestions.length === 0}
					>
						Sana/Valida
					</Button>
				</Modal.Footer>
			</Modal>
			<Modal
				show={isConfirmModalOpen}
				onHide={() => {
					setIsConfirmModalOpen(false);
				}}
				centered
			>
				<Modal.Header closeButton>
					<Modal.Title>Attenzione</Modal.Title>
				</Modal.Header>
				<Modal.Body>
					<div className="my-3">
						<span>Sei sicuro di voler procedere? Gli interventi sanatori non potranno essere più modificati.</span>
					</div>
					{statusBar.status ? (
						<StatusBar status={statusBar.status} message={statusBar.message} timeout={statusBar.timeout}>
							<></>
						</StatusBar>
					) : null}
				</Modal.Body>
				<Modal.Footer>
					<Button variant="primary" onClick={handleSettleQuestions}>
						Conferma
					</Button>
				</Modal.Footer>
			</Modal>
		</>
	) : null;
};

export default PeriodicReportingInfoCell;
